import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Loader, AlertCircle, CheckCircle2, Check, X } from 'lucide-react';
import { sendWhatsappCode, validateWhatsappCode, changeUserMobile } from '../services/outletApi';

// Port del WhatsappVerificationModal de consulta, MX-only y no bloqueante:
// "Verificar después" cierra el modal sin perder el registro (el lead y el
// dais.user ya existen; phone_verified queda en false).
//
// Fases: 'send' (confirmar número y disparar código) → 'code' (PIN 4 dígitos
// con auto-focus, auto-submit, paste y reenvío con cooldown de 60s).

const PIN_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 60;

const maskPhone = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (digits.length < 4) return rawPhone || '';
  return `••• ••• ${digits.slice(-4)}`;
};

export default function WhatsappVerifyModal({ userId, authToken, phone, onVerified, onSkip }) {
  const [phase, setPhase] = useState('send'); // 'send' | 'code'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [notificationId, setNotificationId] = useState(null);
  const [pin, setPin] = useState(['', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [currentPhone, setCurrentPhone] = useState(phone || '');
  const [editing, setEditing] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [changingPhone, setChangingPhone] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const t = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  useEffect(() => {
    if (phase === 'code') {
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    }
  }, [phase]);

  const handleSendCode = async () => {
    setLoading(true);
    setErrorMsg('');
    const res = await sendWhatsappCode({ authToken, userId });
    setLoading(false);
    if (!res.success) {
      setErrorMsg(res.error || 'No se pudo enviar el código.');
      return;
    }
    setNotificationId(res.notificationId);
    setPin(['', '', '', '']);
    setPhase('code');
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
  };

  const handleChangePhone = async () => {
    const clean = newPhone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(clean)) {
      setPhoneError('Ingresa un número de 10 dígitos válido.');
      return;
    }
    setChangingPhone(true);
    setPhoneError('');
    const res = await changeUserMobile({ authToken, userId, mobile: clean });
    setChangingPhone(false);
    if (!res.success) {
      setPhoneError(res.error || 'No se pudo actualizar el número. Intenta de nuevo.');
      return;
    }
    setCurrentPhone(clean);
    setEditing(false);
    setNewPhone('');
    setErrorMsg('');
  };

  const submitCode = async (codeArr) => {
    const code = codeArr.join('');
    if (code.length < PIN_LENGTH || loading || !notificationId) return;
    setLoading(true);
    setErrorMsg('');
    const res = await validateWhatsappCode({ authToken, userId, code, notificationId });
    setLoading(false);
    if (!res.success) {
      setErrorMsg(res.error || 'Código incorrecto. Intenta de nuevo.');
      setPin(['', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
      return;
    }
    onVerified?.();
  };

  const handlePinChange = (idx, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...pin];
    next[idx] = digit;
    setPin(next);
    if (errorMsg) setErrorMsg('');
    if (digit && idx < PIN_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
    if (next.every((d) => d.length === 1)) {
      submitCode(next);
    }
  };

  const handlePinKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !pin[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    const digits = text.replace(/\D/g, '').slice(0, PIN_LENGTH).split('');
    if (digits.length === 0) return;
    const next = ['', '', '', ''];
    digits.forEach((d, i) => { next[i] = d; });
    setPin(next);
    if (digits.length === PIN_LENGTH) {
      submitCode(next);
    } else {
      inputRefs.current[digits.length]?.focus();
    }
  };

  return (
    <div className="wam-overlay animate-fadeIn">
      <div className="wam-card">
        <div className="wam-header">
          <div className="wam-icon"><MessageCircle size={24} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="wam-title">
              {phase === 'send' ? 'Verifica tu WhatsApp' : 'Ingresa el código'}
            </h3>
            <p className="wam-sub">
              {phase === 'send'
                ? 'Por ahí te avisaremos cuando se abra un horario para ti.'
                : `Enviamos un código de 4 dígitos a ${maskPhone(currentPhone) || 'tu WhatsApp'}.`}
            </p>
          </div>
        </div>

        <div className="wam-body">
          {phase === 'send' && (
            <>
              <div className="wam-phone-box">
                <p className="ds-caption" style={{ marginBottom: 4 }}>Tu número</p>
                <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
                  +52 {maskPhone(currentPhone) || 'Sin número'}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <CheckCircle2 size={15} color="var(--daisies)" style={{ flexShrink: 0, marginTop: 1 }} />
                  Te llegará un mensaje de WhatsApp con un código de 4 dígitos.
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <CheckCircle2 size={15} color="var(--daisies)" style={{ flexShrink: 0, marginTop: 1 }} />
                  Solo se verifica una vez; así sabemos que los avisos sí te llegan.
                </span>
              </div>

              {errorMsg && (
                <div className="wam-error">
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  {errorMsg}
                </div>
              )}

              {editing ? (
                <div className="wam-phone-box" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Nuevo número (10 dígitos)</p>
                  <div className="phone-input-group">
                    <div className="phone-prefix"><span className="flag">🇲🇽</span><span>+52</span></div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="input-field"
                      placeholder="10 dígitos"
                      value={newPhone}
                      autoFocus
                      disabled={changingPhone}
                      onChange={(e) => { setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setPhoneError(''); }}
                    />
                  </div>
                  {phoneError && <p className="form-error">{phoneError}</p>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn-secondary press-scale" style={{ flex: 1 }} disabled={changingPhone} onClick={() => { setEditing(false); setNewPhone(''); setPhoneError(''); }}>
                      <X size={15} /> Cancelar
                    </button>
                    <button type="button" className="btn-primary press-scale" style={{ flex: 1 }} disabled={changingPhone || newPhone.length < 10} onClick={handleChangePhone}>
                      {changingPhone ? <Loader size={15} className="spin" /> : <Check size={15} />}
                      Guardar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button type="button" className="btn-primary press-scale" onClick={handleSendCode} disabled={loading}>
                    {loading ? <Loader size={17} className="spin" /> : <MessageCircle size={17} />}
                    {loading ? 'Enviando...' : 'Enviar código por WhatsApp'}
                  </button>
                  <button type="button" className="wam-link" onClick={() => { setEditing(true); setNewPhone(''); setPhoneError(''); }} disabled={loading}>
                    Cambiar mi número
                  </button>
                </>
              )}
            </>
          )}

          {phase === 'code' && (
            <>
              <div className="wam-pin-row" onPaste={handlePinPaste}>
                {pin.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { inputRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    disabled={loading}
                    onChange={(e) => handlePinChange(idx, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(idx, e)}
                    className={`wam-pin-input ${errorMsg ? 'wam-pin-input--error' : digit ? 'wam-pin-input--filled' : ''}`}
                  />
                ))}
              </div>

              {errorMsg && (
                <div className="wam-error">
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  {errorMsg}
                </div>
              )}

              {loading && (
                <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                  Validando código...
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <button type="button" className="wam-link" onClick={handleSendCode} disabled={resendCountdown > 0 || loading}>
                  {resendCountdown > 0 ? `Reenviar en ${resendCountdown}s` : 'Reenviar código'}
                </button>
                <button type="button" className="wam-link" style={{ fontSize: 12, color: 'var(--text-secondary)' }} onClick={() => { setPhase('send'); setErrorMsg(''); }} disabled={loading}>
                  ¿Número incorrecto?
                </button>
              </div>
            </>
          )}
        </div>

        <button type="button" className="wam-skip press-scale" onClick={onSkip} disabled={loading}>
          Verificar después
        </button>
      </div>
    </div>
  );
}
